// 密码 AES-256-CBC 传输加密（与后端 PasswordCryptoService 互操作）
// 后端：openssl AES-256-CBC / PKCS7，key 32 字节 base64（SITE_PASSWORD_SECRET_KEY），iv 16 字节 base64（SITE_PASSWORD_IV）
// 前端：VITE_PASSWORD_SECRET_KEY / VITE_PASSWORD_IV（与后端填同一组值）
// 注意：传输加密为纵深防御（HTTPS 已是主防线），密钥随前端包分发，不得视为机密。

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function loadKeyMaterial(): { keyBytes: Uint8Array; ivBytes: Uint8Array } {
  const keyB64 = import.meta.env.VITE_PASSWORD_SECRET_KEY as string | undefined
  const ivB64 = import.meta.env.VITE_PASSWORD_IV as string | undefined
  if (!keyB64 || !ivB64) {
    throw new Error('[YFB][crypto] VITE_PASSWORD_SECRET_KEY / VITE_PASSWORD_IV 未配置')
  }
  const keyBytes = base64ToBytes(keyB64)
  const ivBytes = base64ToBytes(ivB64)
  if (keyBytes.length !== 32 || ivBytes.length !== 16) {
    throw new Error('[YFB][crypto] 密码密钥必须为 32 字节、IV 必须为 16 字节（base64）')
  }
  return { keyBytes, ivBytes }
}

/**
 * AES-256-CBC 加密明文密码，返回 base64 密文（与后端 PasswordCryptoService::decrypt 互操作）
 */
export async function encryptPassword(plain: string): Promise<string> {
  const { keyBytes, ivBytes } = loadKeyMaterial()
  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes as unknown as BufferSource,
    { name: 'AES-CBC' },
    false,
    ['encrypt']
  )
  const data = new TextEncoder().encode(plain)
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv: ivBytes as unknown as BufferSource },
    key,
    data as unknown as BufferSource
  )
  return bytesToBase64(new Uint8Array(encrypted))
}

/**
 * AES-256-CBC 解密密文密码，返回明文（供「记住密码」回填）
 */
export async function decryptPassword(cipherBase64: string): Promise<string> {
  const { keyBytes, ivBytes } = loadKeyMaterial()
  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes as unknown as BufferSource,
    { name: 'AES-CBC' },
    false,
    ['decrypt']
  )
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv: ivBytes as unknown as BufferSource },
    key,
    base64ToBytes(cipherBase64) as unknown as BufferSource
  )
  return new TextDecoder().decode(decrypted)
}
